import React from "react";
import { Grid } from "@mui/material";
import { useWatch } from "react-hook-form";
import EntitySelector from "./entitySelector";
import {
  COMPANY_BUSINESS_OBJECT_ENUM,
  DEAL_BUSINESS_OBJECT_ENUM,
  FIRM_BUSINESS_OBJECT_ENUM,
  FIRM_ENGAGEMENT_BUSINESS_OBJECT_ENUM,
  INVESTMENT_BUSINESS_OBJECT_ENUM,
} from "./constants";
import { toDateInputValue } from "./utils";

interface DocumentEntityInputProps {
  allowChange: boolean;
  index?: number; // Optional - only for document-specific mode
  isOverride?: boolean; // For override mode
  isRequired?: boolean; // Whether the entity selection is required
  autoPopulatedFields?: string[];
  setAutoPopulatedFields?: React.Dispatch<React.SetStateAction<string[]>>;
  /** Grid column span (1-12) for each entity selector. Use 12 in narrow panels so boxes stack. */
  xs?: number;
}

const DocumentEntityInput: React.FC<DocumentEntityInputProps> = ({
  allowChange,
  index,
  isOverride = false,
  isRequired = false,
  autoPopulatedFields = [],
  setAutoPopulatedFields,
  xs = 6,
}) => {
  const prefix = isOverride ? "overrideEntities" : `queues.${index}.entities`;
  const firmRecordKey = `${prefix}.${FIRM_BUSINESS_OBJECT_ENUM}`;

  // The document date lives under a different name in each host form: per-row
  // in Multi-Document Upload, "overrideDate" in its set-for-all panel, and
  // "date" in the single-row queue/viewer edit forms. Watch all of them and
  // take whichever this form actually has — it only pre-fills a create form.
  const dateKeys =
    index != null ? [`queues.${index}.date`] : ["overrideDate", "date"];
  const dateValues = useWatch({ name: dateKeys }) as unknown[];
  const documentDate = toDateInputValue(
    dateValues?.find(value => !!value) ?? null
  );

  const entityTypes = [
    {
      resource: "firm",
      label: "Firm(s)",
      businessObjectEnumId: FIRM_BUSINESS_OBJECT_ENUM,
    },
    {
      resource: "investment",
      label: "Investment(s)",
      businessObjectEnumId: INVESTMENT_BUSINESS_OBJECT_ENUM,
    },
    {
      resource: "company",
      label: "Company(s)",
      businessObjectEnumId: COMPANY_BUSINESS_OBJECT_ENUM,
    },
    {
      resource: "deal",
      label: "Deal(s)",
      businessObjectEnumId: DEAL_BUSINESS_OBJECT_ENUM,
    },
    {
      resource: "firmEngagement",
      label: "Engagement(s)",
      businessObjectEnumId: FIRM_ENGAGEMENT_BUSINESS_OBJECT_ENUM,
    },
  ];

  return (
    <Grid container spacing={1}>
      {entityTypes.map(({ resource, label, businessObjectEnumId }) => {
        const recordKey = `${prefix}.${businessObjectEnumId}`;
        const isLLMPopulated =
          autoPopulatedFields?.includes(recordKey) || false;

        return (
          <Grid item xs={xs} key={resource}>
            <EntitySelector
              resource={resource}
              recordKey={recordKey}
              label={label}
              isRequired={false}
              allowChange={allowChange}
              hidden={false}
              multiple={true}
              businessObjectEnumId={businessObjectEnumId}
              isLLMPopulated={isLLMPopulated}
              autoPopulatedFields={autoPopulatedFields}
              setAutoPopulatedFields={setAutoPopulatedFields}
              documentDate={documentDate}
              firmRecordKey={firmRecordKey}
            />
          </Grid>
        );
      })}
    </Grid>
  );
};

export default DocumentEntityInput;
